import { useRef, useState } from 'react';
import { FieldValues } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';

import dayjs from 'dayjs';
import i18next from 'i18next';

import { SelectionItem } from 'types/api/selection.type';

import { useAddItemMutation } from 'redux/api/ordersApi';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { deleteSelection } from 'redux/slices/selections';
import { setOrderId, setOrderNumber } from 'redux/slices/settings';

import { ordersDataService } from 'services';
import { pdfService } from 'services/pdf.service';
import { selectionDataService } from 'services/selection.service';

import { paths } from 'data/routes';

import { Button } from 'components/Button';
import { InfoModal } from 'components/InfoModal';
import { pickedAddresses } from 'components/OrderAddressBlocks';
import { OrderModal } from 'components/OrderModal';

import * as S from './Actions.styles';

type ActionsProps = {
  id: number;
  name: string;
};

export function Actions(props: ActionsProps): React.ReactElement {
  const { id, name } = { ...props };

  const dispatch = useAppDispatch();

  const ref = useRef(null);

  const { orderNumber, warehouse, season, zone, marketLine } = useAppSelector(
    (state) => state.settings
  );

  const [isInfoModalVisible, isInfoModalVisibleSet] = useState<boolean>(false);
  const [isOrderModalVisible, isOrderModalVisibleSet] = useState<boolean>(false);
  const [isLoading, isLoadingSet] = useState<boolean>(false);
  const [isPDFButtonLoading, isPDFButtonLoadingSet] = useState<boolean>(false);
  const [isPDFDownloadVisible, isPDFDownloadVisibleSet] = useState<boolean>(false);
  const { t } = useTranslation();
  const [addItem] = useAddItemMutation();

  const onOrderSubmit = (data: FieldValues) => {
    if (data.customer) {
      isLoadingSet(true);

      ordersDataService
        .post(season, zone, warehouse.code, data.customer, marketLine, pickedAddresses(data))
        .then((res) => {
          const { orderId, orderNumber: newOrderNumber } = res.data;
          localStorage.setItem('orderNumber', String(newOrderNumber));
          localStorage.setItem('orderId', orderId);
          dispatch(setOrderId(orderId));
          dispatch(setOrderNumber(String(newOrderNumber)));

          selectionDataService
            .get(id.toString(), i18next.language)
            .then((selectionRes) => {
              const addPromises = selectionRes.data.map((item: SelectionItem) =>
                addItem({
                  orderId,
                  data: {
                    productCode: item.itemCodes?.split(' ')[1] ?? '',
                    mainFabricCode: item.mainFabricCode,
                    colorCode: item.itemColorCode,
                    sizes: [],
                  },
                }).unwrap()
              );

              Promise.all(addPromises)
                .then(() => {
                  isLoadingSet(false);
                  toast.success(t('selection.addedToOrder'));
                  isOrderModalVisibleSet(false);
                })
                .catch(() => {
                  isLoadingSet(false);
                  isOrderModalVisibleSet(false);
                  toast.error(t('selection.couldNotAddToOrder'));
                });
            })
            .catch((err: unknown) => {
              isLoadingSet(false);
              isOrderModalVisibleSet(false);

              const error = err as {
                response?: { status?: number; data?: { apierror?: { message?: string } } };
              };

              toast.error(
                error.response?.status === 404
                  ? t('selection.orderCreatedNoItems')
                  : error.response?.data?.apierror?.message ||
                      t('selection.couldNotAddToOrder')
              );
            });
        })
        .catch(() => {
          isLoadingSet(false);
          toast.error(t('order.failedGeneric'));
        });
    }
  };

  const addItemsToOrder = () => {
    if (orderNumber && id && zone) {
      const currentOrderId = localStorage.getItem('orderId') ?? '';

      selectionDataService
        .get(id.toString(), i18next.language)
        .then((res) => {
          const addPromises = res.data.map((item: SelectionItem) =>
            addItem({
              orderId: currentOrderId,
              data: {
                productCode: item.itemCodes?.split(' ')[1] ?? '',
                mainFabricCode: item.mainFabricCode,
                colorCode: item.itemColorCode,
                sizes: [],
              },
            }).unwrap()
          );

          Promise.all(addPromises)
            .then(() => {
              toast.success(t('selection.addedToOrder'));
            })
            .catch(() => toast.error(t('selection.orderFailed')));
        })
        .catch((err: unknown) => {
          const error = err as {
            response?: { status?: number; data?: { apierror?: { message?: string } } };
          };

          toast.error(
            error.response?.status === 404
              ? t('selection.noItemsToOrder')
              : error.response?.data?.apierror?.message || t('selection.orderFailed')
          );
        });
    } else {
      isOrderModalVisibleSet(true);
    }
  };

  const getPDF = (asStylePlan: boolean = false) => {
    isPDFButtonLoadingSet(true);

    selectionDataService
      .get(id.toString(), i18next.language)
      .then((res) => {
        const data = {
          name,
          products: res.data,
          asStylePlan,
        };

        pdfService.getSelectionPDF(data).then((pdfRes) => {
          const url = window.URL.createObjectURL(new Blob([pdfRes.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `COLMAR_CATALOGUE_WISHLIST_${dayjs().toDate()}.pdf`);
          document.body.appendChild(link);
          link.click();
          isPDFButtonLoadingSet(false);
        });
      })
      .catch(() => {
        isPDFButtonLoadingSet(false);
        toast.error(t('selection.pdfNoProducts'));
      });
  };

  useClickAway(ref, () => isPDFDownloadVisibleSet(false));

  return (
    <S.Wrapper>
      <Button
        scale="xs"
        fontFamily="LFT Zeno"
        fontWeight="extraLight"
        asLink
        to={paths.list(id, name)}
        motive="actionSecondary"
      >
        {t('common.show')}
      </Button>

      <Button
        scale="xs"
        fontFamily="LFT Zeno"
        fontWeight="extraLight"
        motive="actionSecondary"
        onClick={() => addItemsToOrder()}
      >
        {t('product.orderButton')}
      </Button>

      <S.ButtonWrapper>
        <Button
          scale="xs"
          fontFamily="LFT Zeno"
          fontWeight="extraLight"
          motive="actionSecondary"
          isLoading={isPDFButtonLoading}
          onClick={() => isPDFDownloadVisibleSet(!isPDFDownloadVisible)}
        >
          {t('common.download')}
        </Button>

        {isPDFDownloadVisible ? (
          <S.Container ref={ref}>
            <Button
              onClick={() => getPDF()}
              motive="whiteSecondary"
              scale="xs"
              fontFamily="LFT Zeno"
            >
              PDF
            </Button>

            <Button
              onClick={() => getPDF(true)}
              motive="whiteSecondary"
              scale="xs"
              fontFamily="LFT Zeno"
            >
              Style plan
            </Button>
          </S.Container>
        ) : undefined}
      </S.ButtonWrapper>

      <Button
        motive="cancelPrimary"
        iconLeft="FiX"
        onClick={() => {
          isInfoModalVisibleSet(true);
        }}
      />

      {isInfoModalVisible ? (
        <InfoModal
          title={t('common.delete')}
          info={t('common.deleteListConfirm')}
          onClose={() => isInfoModalVisibleSet(false)}
          actionText={t('common.delete')}
          onAction={() => {
            selectionDataService.deleteList(id).then(() => {
              toast.success(t('selection.listDeleted'));
              isInfoModalVisibleSet(false);
              dispatch(deleteSelection(id));
            });
          }}
        />
      ) : undefined}

      {isOrderModalVisible ? (
        <OrderModal
          onClose={() => isOrderModalVisibleSet(false)}
          onSubmit={onOrderSubmit}
          isLoading={isLoading}
        />
      ) : undefined}
    </S.Wrapper>
  );
}
